// src/lib/menage-trace-derniere-main.js
//
// QUI A TRANCHÉ EN DERNIER SUR CE MÉNAGE — règles PURES d'affichage.
//
// Depuis que l'admin peut piloter la même frise que son orchestrateur, deux
// mains peuvent déplacer le même bloc. « La dernière modification fait foi »
// (Dimitri) ne tient que si on peut la LIRE.
//
// ⚠️ « MODIFIÉ PAR », JAMAIS « HEURE FIXÉE PAR ». La base estampille
// `modifie_par` dès que l'heure, la durée OU l'agent bougent, sans noter
// lequel : nommer le geste serait faux une fois sur trois. Le détail au clic
// montre l'état, celui qui veut savoir quoi l'ouvre (décision Dimitri
// 2026-08-12 — pas de colonne supplémentaire pour ça).
//
// ⚠️ AUCUN MONTANT, et `modifie_par` est un email d'ÉQUIPE, jamais un voyageur.

/** Le prénom, à partir de l'email d'équipe. Jamais l'adresse en clair. */
export function prenomDeEmail(email) {
  const brut = String(email || '').split('@')[0] || ''
  const p = brut.split(/[.\-_+]/)[0]
  if (!p) return null
  return p.charAt(0).toUpperCase() + p.slice(1)
}

/**
 * QUAND, EN COURT : « aujourd'hui 14:32 », « hier 09:15 », « lundi 11:40 ».
 *
 * ⚠️ PAS DE DATE COMPLÈTE POUR QUELQUE CHOSE DE RÉCENT (consigne Dimitri) :
 * « 12/08/2026 14:32 » oblige à calculer ce que « aujourd'hui » dit tout de
 * suite. Au-delà d'une semaine en revanche, le nom du jour ne situe plus rien —
 * « lundi » à trois semaines est pire qu'une date.
 */
export function quandCourt(iso, maintenant) {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const ref = maintenant instanceof Date ? maintenant : new Date()

  const heure = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
  }).format(d)
  const jourDe = (x) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(x)
  const [ay, am, ad] = jourDe(d).split('-').map(Number)
  const [by, bm, bd] = jourDe(ref).split('-').map(Number)
  const ecart = Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000)

  if (ecart === 0) return `aujourd'hui ${heure}`
  if (ecart === 1) return `hier ${heure}`
  if (ecart > 1 && ecart < 7) {
    const jour = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', timeZone: 'Europe/Paris' }).format(d)
    return `${jour} ${heure}`
  }
  // Futur (horloge décalée) ou plus d'une semaine : la date, qui situe vraiment.
  const date = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' }).format(d)
  return `${date} ${heure}`
}

/** La phrase complète du détail — `null` si aucune trace (cas de tout l'historique). */
export function phraseTrace(run, maintenant) {
  const qui = prenomDeEmail(run?.modifie_par)
  const quand = quandCourt(run?.modifie_le, maintenant)
  if (!qui || !quand) return null
  return `Modifié par ${qui} · ${quand}`
}

/**
 * FAUT-IL LA MONTRER SUR LA LIGNE ELLE-MÊME ?
 *
 * 🔴 TROIS CONDITIONS, ET CHACUNE A SA RAISON :
 *  · une trace existe ;
 *  · elle n'est PAS de moi — ma propre modification ne m'apprend rien, c'est
 *    celle de l'autre qui peut me surprendre ;
 *  · elle date du JOUR — une décision d'il y a trois jours n'est plus une
 *    collision possible, c'est de l'histoire, et l'histoire vit dans le détail ;
 *  · et la ligne ne porte PAS déjà un motif « À régler » — sinon les deux se
 *    disputent le même emplacement et l'information qui n'appelle aucune action
 *    passerait devant celle qui en appelle une. C'est la protection explicite
 *    du signal qu'on venait de construire.
 * CASSER POUR VÉRIFIER : retirer le test du motif → la trace masque « sans agent ».
 */
export function traceSurLaLigne({ run, moi, motif, maintenant } = {}) {
  if (motif) return null
  const auteur = run?.modifie_par
  if (!auteur) return null
  if (moi && String(auteur).toLowerCase() === String(moi).toLowerCase()) return null
  const quand = quandCourt(run?.modifie_le, maintenant)
  if (!quand || !quand.startsWith("aujourd'hui")) return null
  return phraseTrace(run, maintenant)
}
